import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faShieldAlt,
  faBug,
  faUserShield,
  faClipboardCheck,
  faSignInAlt,
  faTimes,
  faUser,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import "../../styles/circuit-background.css";

const ReportLanding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showReportTypeModal, setShowReportTypeModal] = useState(false);

  const reportTypes = [
    {
      id: "incident",
      title: t("reportLanding.reportTypes.incident.title"),
      description: t("reportLanding.reportTypes.incident.description"),
      icon: faExclamationTriangle,
      color: "from-red-500 to-red-600",
      path: "/report-incident/form"
    },
    {
      id: "vulnerability",
      title: t("reportLanding.reportTypes.vulnerability.title"),
      description: t("reportLanding.reportTypes.vulnerability.description"),
      icon: faShieldAlt,
      color: "from-orange-500 to-orange-600",
      path: "/report-vulnerability/form"
    },
    {
      id: "malware",
      title: t("reportLanding.reportTypes.malware.title"),
      description: t("reportLanding.reportTypes.malware.description"),
      icon: faBug,
      color: "from-green-500 to-green-600",
      path: "/report-malware/form"
    },
    {
      id: "child-protection",
      title: t("reportLanding.reportTypes.childProtection.title"),
      description: t("reportLanding.reportTypes.childProtection.description"),
      icon: faUserShield,
      color: "from-blue-500 to-blue-600",
      path: "/report-cop/stepped"
    },
    {
      id: "online-abuse",
      title: t("reportLanding.reportTypes.onlineAbuse.title"),
      description: t("reportLanding.reportTypes.onlineAbuse.description"),
      icon: faBug,
      color: "from-purple-500 to-purple-600",
      path: "/report-abuse/form"
    }
  ];

  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t("reportLanding.title")}
        description={t("reportLanding.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.report_incident"), link: null },
        ]}
        backgroundIcons={[faExclamationTriangle, faShieldAlt, faBug, faClipboardCheck]}
      />

      <div className="min-h-screen bg-gray-50 py-16 font-nunito-sans">
        <div className="px-dynamic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full"
          >
            {/* Welcome Section */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="text-left">
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  {t("reportLanding.welcomeText")}
                </p>

                {/* Track Reports Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {t("reportLanding.trackSection.title")}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t("reportLanding.trackSection.description")}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/track-report"
                      className="flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" />
                      {t("reportLanding.trackSection.button")}
                    </Link>
                    {/* <button 
                      onClick={() => setShowComingSoon(true)}
                      className="flex items-center justify-center px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <FontAwesomeIcon icon={faSignInAlt} className="mr-2" />
                      Login (Coming Soon)
                    </button> */}
                  </div>
                </div>

                {/* <p className="text-gray-600 text-center mb-6">
                  {t("reportLanding.loginSection.orText")}
                </p> */}
              </div>
            </div>

            {/* Report Types Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {reportTypes.map((type, index) => (
                <motion.div
                  key={type.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => {
                    if (type.id === "incident") {
                      setShowReportTypeModal(true);
                    } else {
                      navigate(type.path);
                    }
                  }}
                  className="circuit-card bg-white rounded-xl shadow-lg p-6 cursor-pointer transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-100 relative overflow-hidden"
                >
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${type.color} flex items-center justify-center mb-4 relative z-20`}>
                    <FontAwesomeIcon icon={type.icon} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 relative z-20">{type.title}</h3>
                  <p className="text-gray-600 relative z-20">{type.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Instructions Section */}
            {/* <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-left">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  {t("reportLanding.instructions.title")}
                </h3>
                <div className="space-y-6">
                  <p className="text-gray-700">
                    {t("reportLanding.instructions.completeness")}
                  </p>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400 mr-3 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-800 mb-2">{t("reportLanding.instructions.warning.title")}</p>
                        <p className="text-gray-700">
                          {t("reportLanding.instructions.warning.text")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700">
                    {t("reportLanding.instructions.privacy")}
                  </p>
                </div>
              </div>
            </div> */}
          </motion.div>
        </div>
      </div>

      {/* Report Type Selection Modal */}
      {showReportTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-4"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">{t("incident report.ui.select_report_type")}</h3>
              <button
                onClick={() => setShowReportTypeModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => {
                  navigate("/report-incident/form?type=organization");
                  setShowReportTypeModal(false);
                }}
                className="w-full flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faBuilding} className="text-white text-xl" />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-800">{t("incident report.ui.organization_report")}</h4>
                  <p className="text-sm text-gray-600">{t("incident report.ui.organization_report_description")}</p>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate("/report-incident/form?type=personal");
                  setShowReportTypeModal(false);
                }}
                className="w-full flex items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faUser} className="text-white text-xl" />
                </div>
                <div className="text-left">
                  <h4 className="font-semibold text-gray-800">{t("incident report.ui.personal_report")}</h4>
                  <p className="text-sm text-gray-600">{t("incident report.ui.personal_report_description")}</p>
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Coming Soon Popup */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-4"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">{t("reportLanding.comingSoon.title")}</h3>
              <button
                onClick={() => setShowComingSoon(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-gray-700 text-center">
                {t("reportLanding.comingSoon.message")}
              </p>
            </div>
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowComingSoon(false)}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                {t("reportLanding.comingSoon.ok")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ReportLanding;